<template>
  <div class="legal-document-container max-w-4xl mx-auto pb-6 px-6 bg-white shadow-lg rounded-xl">
    
    <div v-if="availableLanguages.length" class="top-0 z-20 bg-white pt-6 pb-4 border-b border-gray-100">
      <div class="flex justify-center">
        <div class="inline-flex p-1 bg-gray-100 rounded-lg">
          <button
            v-for="language in availableLanguages"
            :key="language.key"
            type="button"
            @click="activeLang = language.key"
            :class="['px-6 py-2 text-sm font-bold rounded-md transition-all', activeLang === language.key ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700']"
          >
            {{ language.label }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex items-center justify-center p-8 text-center text-sm italic text-gray-500"
    >
      {{ t('view.rg_standardtexts_empty') }}
    </div>

    <section v-for="group in versionGroups" :key="group.date" class="mb-8">
      <button
        type="button"
        class="version-heading sticky z-30 h-16 w-full bg-white text-left border-b-2 border-gray-100"
        @click="toggleVersion(group.date)"
        :aria-expanded="isVersionOpen(group.date)"
        :aria-controls="`version-${String(group.date)}`"
      >
        <div class="flex h-full items-center justify-between gap-4">
          <div class="flex min-w-0 items-center gap-2">
            <span class="ml-4 shrink-0 px-2 py-1 bg-blue-50 text-blue-600 text-[10px] rounded uppercase font-bold tracking-widest">
              Version
            </span>
            <h2 class="truncate text-xl font-bold text-blue-900">{{ formatDate(group.date) }}</h2>
          </div>
          <span
            class="mr-4 text-blue-900 transition-transform duration-200"
            :class="isVersionOpen(group.date) ? 'rotate-180' : 'rotate-0'"
            aria-hidden="true"
          >
            ▼
          </span>
        </div>
      </button>

      <transition name="accordion">
        <div v-show="isVersionOpen(group.date)" :id="`version-${String(group.date)}`">
          <div v-for="entry in group.entries" :key="entryKey(entry)" class="document-section relative rounded-xl border border-gray-100">
            <button
              type="button"
              class="section-heading sticky z-20 flex w-full items-center justify-between gap-4 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
              @click="toggleEntry(entryKey(entry))"
              :aria-expanded="isEntryOpen(entryKey(entry))"
              :aria-controls="`entry-${String(entryKey(entry))}`"
            >
              <span class="text-sm font-bold text-gray-700 uppercase tracking-wide">
                {{ titleForLanguage(entry, activeLang) }}
              </span>
              <span
                class="text-gray-500 transition-transform duration-200"
                :class="isEntryOpen(entryKey(entry)) ? 'rotate-180' : 'rotate-0'"
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <transition name="accordion">
              <div v-show="isEntryOpen(entryKey(entry))" :id="`entry-${String(entryKey(entry))}`" class="overflow-hidden rounded-b-xl bg-white px-4 py-4">
                <div class="animate-fadeIn">
                  <div
                    class="prose prose-blue max-w-none text-gray-800"
                    v-html="renderMarkdown(textForLanguage(entry, activeLang))"
                  ></div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue';
import { marked } from 'marked';
import { useI18n } from 'vue-i18n';
import { compareStandardTextsByDocumentLevel, taiwanParagraphTitle } from '@/utils/standardTexts';

const { t } = useI18n();

marked.setOptions({
  breaks: true,
  gfm: true
});

const props = defineProps({
  standardTexts: {
    type: Array,
    required: true
  },
  isTaiwan: {
    type: Boolean,
    default: false
  }
});

const activeLang = ref('de');

const openVersions = ref({});
const openEntries = ref({});

const isVersionOpen = (date) => !!openVersions.value[String(date)];
const toggleVersion = (date) => {
  const key = String(date);
  openVersions.value = { ...openVersions.value, [key]: !openVersions.value[key] };
};

const isEntryOpen = (entryId) => !!openEntries.value[String(entryId)];
const toggleEntry = (entryId) => {
  const key = String(entryId);
  openEntries.value = { ...openEntries.value, [key]: !openEntries.value[key] };
};

const entryKey = (entry) => entry?.id ?? entry?.id_norm

const languageDefinitions = [
  { key: 'de', label: 'Deutsch', titleField: 'title_de', fields: ['text_de_markdown', 'text_de'] },
  { key: 'en', label: 'English', titleField: 'title_en', fields: ['text_en_markdown', 'text_en'] },
  { key: 'orig', label: 'Original', titleField: 'title_orig', fields: ['text_orig_markdown', 'text_orig'] }
];

const fallbackTitle = (entry) => [
  entry?.document_level_description,
  entry?.document_level
].filter((value) => String(value ?? '').trim()).join(' ');

const titleForLanguage = (entry, languageKey) => {
  const articleTitle = props.isTaiwan ? taiwanParagraphTitle(entry) : null;
  if (articleTitle) return articleTitle;

  const language = languageDefinitions.find(({ key }) => key === languageKey);
  const title = language ? entry?.[language.titleField] : null;

  return title?.trim() || fallbackTitle(entry);
};

const textForLanguage = (entry, languageKey) => {
  const language = languageDefinitions.find(({ key }) => key === languageKey);
  if (!language) return '';

  return language.fields.map((field) => entry?.[field]).find((text) => text?.trim()) || '';
};

const hasTextForLanguage = (entry, languageKey) => !!textForLanguage(entry, languageKey);

const availableLanguages = computed(() => {
  const list = Array.isArray(props.standardTexts) ? props.standardTexts : [];
  return languageDefinitions.filter(({ key }) => list.some((entry) => hasTextForLanguage(entry, key)));
});

watchEffect(() => {
  if (!availableLanguages.value.some(({ key }) => key === activeLang.value)) {
    activeLang.value = availableLanguages.value[0]?.key || 'de';
  }
});

// Group + sort by version date (descending). "Unknown" ends up last.
const versionGroups = computed(() => {
  const list = Array.isArray(props.standardTexts)
    ? props.standardTexts.filter((entry) => hasTextForLanguage(entry, activeLang.value))
    : [];
  const grouped = list.reduce((acc, obj) => {
    const key = obj?.version_date || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(obj);
    return acc;
  }, {});

  const parseKey = (key) => (key === 'Unknown' ? Number.NEGATIVE_INFINITY : new Date(key).getTime());

  return Object.entries(grouped)
    .sort(([a], [b]) => parseKey(b) - parseKey(a))
    .map(([date, entries]) => ({
      date,
      entries: [...entries].sort(compareStandardTextsByDocumentLevel)
    }));
});

const kramdownStyleAttribute = /\{:\s*style\s*=\s*(["'])(.*?)\1\s*\}/gi;

const isResponsiveImageStyle = (style) => {
  const declarations = style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator === -1) return null;

      return [
        declaration.slice(0, separator).trim().toLowerCase(),
        declaration.slice(separator + 1).trim().toLowerCase()
      ];
    });

  if (declarations.length !== 2 || declarations.some((declaration) => !declaration)) return false;

  const properties = new Map(declarations);
  return properties.size === 2 && properties.get('max-width') === '100%' && properties.get('height') === 'auto';
};

const normalizeMarkdown = (text) => text.replace(
  kramdownStyleAttribute,
  (attribute, _quote, style) => isResponsiveImageStyle(style) ? '' : attribute
);

const renderMarkdown = (text) => {
  if (!text) return '';
  return marked.parse(normalizeMarkdown(text));
};

const formatDate = (dateStr) => {
  if (dateStr === 'Unknown') return dateStr;
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
};
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

.version-heading {
  /* Cancel the full-text scroll area's 1rem top padding. */
  top: -1rem;
}

.section-heading {
  /* Follow the shifted 4rem version header and overlap its bottom border by one pixel. */
  top: calc(3rem - 1px);
  box-shadow: 0 -1px 0 rgb(249 250 251);
}

.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 200ms ease, opacity 200ms ease;
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 2000px;
  opacity: 1;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Typography styles for the rendered Markdown */
:deep(.prose h2) {
  @apply text-lg font-extrabold text-gray-900 border-b border-gray-100 pb-1 mb-4;
}
:deep(.prose p) {
  @apply leading-relaxed mb-4;
}
:deep(.prose img) {
  max-width: 100%;
  height: auto;
}
</style>
